import { forwardRef, useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import type { FocusEvent, PropsWithoutRef, ChangeEvent } from 'react';

import { useConsolidatedRef, useEvent } from '../../../hooks';
import type { ForwardRefForwardPropsComponent, OmitStrict } from '../../../types';
import type { InputProps } from '../../Input/Input';

import { BareInput } from './DateTime.styles';

const regexPattern = /[0-9]/;

const padNumberWithZeros = (maxLength: number, value: string = '') => {
  if (value === '') return '';

  return value.padStart(Number.isFinite(maxLength) ? maxLength : 0, '0');
};
export interface PartInputProps
  extends OmitStrict<InputProps, 'defaultValue' | 'value' | 'onChange'> {
  value?: number;
  min?: number;
  max?: number;
  maxLength?: number;
  padWithZeros?: boolean;
  size?: number;
  onChange?: (value?: string) => void;
}

const PartInput: ForwardRefForwardPropsComponent<PartInputProps> = forwardRef(function PartInput(
  {
    value,
    onChange,
    readOnly,
    min = 0,
    max = Infinity,
    size = 2,
    maxLength = Infinity,
    padWithZeros = true,
    ...restProps
  }: PropsWithoutRef<PartInputProps>,
  ref: PartInputProps['ref']
) {
  const inputRef = useConsolidatedRef(ref);
  // Track whether the input value has been modified by the user.
  // Due to the nature of the input, we must track this through a ref instead of state.
  const modifiedRef = useRef(false);

  useEffect(() => {
    if (inputRef.current && !Number.isNaN(value)) {
      inputRef.current.value = padNumberWithZeros(maxLength, value?.toString());
      modifiedRef.current = false;
    }
  }, [value, maxLength]);

  useLayoutEffect(() => {
    if (inputRef.current) {
      inputRef.current.style.width = `calc(${size}ch + ${size} * ${getComputedStyle(inputRef.current).letterSpacing})`;
    }
  }, [size]);

  useEvent(
    'beforeinput',
    (event: InputEvent) => {
      if (event.data && !regexPattern.test(event.data)) {
        event.preventDefault();
      }
    },
    { target: inputRef }
  );

  const onInputChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const targetEl = e.target;
      if (targetEl.value.length > maxLength) targetEl.value = targetEl.value.slice(0, maxLength);
      else if (size !== maxLength)
        targetEl.style.width = `${Math.max(targetEl.value.length, size)}ch`;
      modifiedRef.current = true;
    },
    [maxLength, size]
  );

  const onBlur = useCallback(
    (e: FocusEvent<HTMLInputElement>) => {
      const targetElValue = e.target.value;
      const paddedValue = padWithZeros
        ? padNumberWithZeros(maxLength, targetElValue)
        : targetElValue;
      e.target.value = paddedValue;
      if (modifiedRef.current) {
        // Only trigger onChange if the value has been modified
        onChange?.(paddedValue);
        modifiedRef.current = false;
      }
    },
    [onChange, padWithZeros, maxLength]
  );

  const defaultValue = value !== undefined ? padNumberWithZeros(maxLength, value?.toString()) : '';

  return (
    <BareInput
      type='number'
      defaultValue={defaultValue}
      onBlur={onBlur}
      onChange={onInputChange}
      step={1}
      min={min}
      max={max}
      maxLength={maxLength}
      readOnly={readOnly}
      ref={inputRef}
      size={size}
      autoComplete='_off'
      {...restProps}
    />
  );
});

export default PartInput;
